import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type JSX } from 'react';

import {
  BulkMachineCard,
  type BulkRowDraft,
  type BulkRowState,
} from '@/components/logsheets-maker/bulk/BulkMachineCard';
import type { BulkScheduleValues } from '@/components/logsheets-maker/bulk/BulkScheduleStep';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TimeField } from '@/components/ui/time-field';
import { uploadStagedFiles } from '@/lib/logsheets-maker/uploadStagedFiles';
import { toast } from '@/lib/toast';
import {
  getMakerKpisForWorkOrderMachineRequest,
  listLogsheetsMakerRequest,
  BulkValidationError,
} from '@/services/logsheets-maker';
import type {
  BulkCreateResult,
  BulkRowBody,
  BulkRowError,
  KpiValuesShape,
  MakerKpiForMachine,
  MakerWorkOrderListItem,
} from '@/types/logsheets-maker';

/**
 * Phase 24 (v1.4) — BulkMachineStep
 *
 * Step 4 of the bulk-create wizard. Owns:
 *  - Per-machine state map (skip / work / idle + draft body)
 *  - REVIEW FP-008: Parallel KPI fetch per machine via TanStack's `useQueries`
 *    (was: raw `Promise.allSettled` outside the cache). The new shape reuses the
 *    same `['logsheets-maker','kpis-for-machine',woId,machineId]` key used by
 *    `useMakerKpisForWorkOrderMachineQuery` so Back→Next within the wizard
 *    serves from cache (`staleTime: 15_000`) instead of re-firing N requests.
 *    TanStack also handles AbortController on unmount via its queryFn signal
 *    contract, subsuming the FF-028 ask for the KPI side of the step.
 *  - Optional duplicate-check via the existing maker list endpoint
 *    (REVIEW FF-028: now threads AbortSignal so cancelling step 4 cancels
 *     the still-in-flight list call rather than letting it burn bandwidth
 *     server-side).
 *  - The sticky bottom CTA ("Save N Drafts")
 *  - Mapping the response 400 BulkValidationError back to per-card errors.
 */

export interface BulkMachineStepProps {
  readonly workOrder: MakerWorkOrderListItem;
  readonly schedule: BulkScheduleValues;
  /** Pass first site on the WO; null disables duplicate-check. */
  readonly siteIdForDuplicateCheck: number | null;
  /**
   * Parent owns the bulk POST. Throws BulkValidationError on row errors.
   * Returns the BulkCreateResult so this step can run the per-row staged-
   * file uploads against the new entry ids.
   */
  readonly onSubmit: (rows: readonly BulkRowBody[]) => Promise<BulkCreateResult>;
  /**
   * Called after the bulk POST AND any per-row staged-file uploads finish.
   * Parent uses this to invalidate caches + navigate. Receives the original
   * BulkCreateResult so the parent can build a routed URL.
   */
  readonly onComplete: (result: BulkCreateResult) => void;
  readonly onBack: () => void;
  /**
   * Lets this step edit the batch-level startTime / endTime — moved here from
   * step 3 so step 3 is one decision (date + shift). Batch-level applies to
   * every row (backend contract Q7).
   */
  readonly onScheduleChange: (next: BulkScheduleValues) => void;
}

function emptyRow(): BulkRowDraft {
  return {
    workDescription: '',
    idleReason: '',
    notes: '',
    kpiOverrides: {},
    stagedFiles: [],
  };
}

type KpisByMachine = Readonly<Record<number, readonly MakerKpiForMachine[] | undefined>>;

type DuplicatesByMachine = Readonly<Record<number, string | null>>;

/** REVIEW FF-022: BulkMachineCard now keeps the raw string while the user is
 *  typing so "1." / "1.5" survive re-renders. Coerce here at submit time —
 *  if the string parses to a finite number, send it as a number; otherwise
 *  pass the string through so the backend validator can surface a clear
 *  message. */
function coerceOverride(v: number | string | null): number | string | null {
  if (v === null) return null;
  if (typeof v === 'number') return v;
  if (v === '' || v === '-' || v === '.' || v === '-.') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
}

function buildKpiValuesForRow(
  kpis: readonly MakerKpiForMachine[] | undefined,
  draft: BulkRowDraft,
): KpiValuesShape | null {
  if (kpis === undefined) {
    // KPIs haven't loaded yet — send any overrides the user provided.
    const merged: Record<string, number | string | null> = {};
    for (const [k, v] of Object.entries(draft.kpiOverrides)) {
      merged[k] = coerceOverride(v);
    }
    return Object.keys(merged).length === 0 ? null : merged;
  }
  const out: Record<string, number | string | null> = {};
  for (const kpi of kpis) {
    const key = `kpi:${String(kpi.id)}`;
    const override = draft.kpiOverrides[key];
    if (override !== undefined) {
      out[key] = coerceOverride(override);
    } else if (kpi.targetPerShift !== null && Number.isFinite(kpi.targetPerShift)) {
      out[key] = kpi.targetPerShift;
    }
    // KPIs with no override AND no target are omitted from the row payload —
    // backend treats missing keys as "no value" and Maker can fill later.
  }
  return Object.keys(out).length === 0 ? null : out;
}

export function BulkMachineStep({
  workOrder,
  schedule,
  siteIdForDuplicateCheck,
  onSubmit,
  onComplete,
  onBack,
  onScheduleChange,
}: BulkMachineStepProps): JSX.Element {
  const machines = workOrder.machines;

  const [states, setStates] = useState<Record<number, BulkRowState>>(() => {
    const init: Record<number, BulkRowState> = {};
    for (const m of machines) init[m.machineId] = 'skip';
    return init;
  });

  const [rows, setRows] = useState<Record<number, BulkRowDraft>>(() => {
    const init: Record<number, BulkRowDraft> = {};
    for (const m of machines) init[m.machineId] = emptyRow();
    return init;
  });

  const [duplicates, setDuplicates] = useState<DuplicatesByMachine>({});
  const [rowErrors, setRowErrors] = useState<Record<number, BulkRowError | null>>({});
  const [bulkErrors, setBulkErrors] = useState<readonly BulkRowError[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [timingError, setTimingError] = useState<string | null>(null);

  const startTimeHm = schedule.startTime;
  const endTimeHm = schedule.endTime ?? '';

  function clearTimingError(): void {
    if (timingError !== null) setTimingError(null);
  }

  function updateStartTime(next: string): void {
    clearTimingError();
    onScheduleChange({ ...schedule, startTime: next });
  }

  function updateEndTime(next: string): void {
    clearTimingError();
    onScheduleChange({
      ...schedule,
      endTime: next === '' ? null : next,
    });
  }

  // REVIEW FP-008: parallel KPI fetch via TanStack's `useQueries`. Same
  // shape as `useMakerKpisForWorkOrderMachineQuery` so the cache is shared
  // and Back→Next within the wizard serves from the 15s staleTime instead
  // of re-firing N HTTP calls. On row fetch error the row falls back to
  // `undefined` (treated as "KPIs haven't loaded" by `buildKpiValuesForRow`)
  // — matching the previous `Promise.allSettled` semantics where a rejected
  // promise set the row to `[]`.
  const kpiQueries = useQueries({
    queries: machines.map((m) => ({
      queryKey: ['logsheets-maker', 'kpis-for-machine', workOrder.id, m.machineId] as const,
      queryFn: () => getMakerKpisForWorkOrderMachineRequest(workOrder.id, m.machineId),
      staleTime: 15_000,
    })),
  });

  const kpisByMachine = useMemo<KpisByMachine>(() => {
    const out: Record<number, readonly MakerKpiForMachine[] | undefined> = {};
    machines.forEach((m, i) => {
      const q = kpiQueries[i];
      if (q === undefined) {
        out[m.machineId] = undefined;
      } else if (q.isError) {
        // Treat fetch failures as empty KPI list so the row remains usable —
        // mirrors the old Promise.allSettled `next[v.machineId] = []` branch.
        out[m.machineId] = [];
      } else {
        out[m.machineId] = q.data;
      }
    });
    return out;
  }, [machines, kpiQueries]);

  // Optional duplicate-check via the existing list endpoint. REVIEW FF-028:
  // thread an AbortController.signal so the in-flight call is cancelled when
  // the effect re-runs (entryDate change) or the component unmounts (user
  // navigates away from step 4). The previous `cancelled` flag suppressed
  // the state-write but the HTTP request still ran to completion and the
  // backend paid for it.
  useEffect(() => {
    if (siteIdForDuplicateCheck === null) {
      setDuplicates({});
      return;
    }
    const ctrl = new AbortController();
    void listLogsheetsMakerRequest(
      {
        siteId: siteIdForDuplicateCheck,
        fromDate: schedule.entryDate,
        toDate: schedule.entryDate,
        pageSize: 50,
      },
      { signal: ctrl.signal },
    )
      .then((result) => {
        if (ctrl.signal.aborted) return;
        const next: Record<number, string | null> = {};
        for (const item of result.items) {
          // Last-write-wins is fine; we just need *some* status string per machine.
          next[item.machineId] = item.status;
        }
        setDuplicates(next);
      })
      .catch(() => {
        // Duplicate-check is best-effort — silently skip on failure or abort.
        if (!ctrl.signal.aborted) setDuplicates({});
      });
    return () => {
      ctrl.abort();
    };
  }, [siteIdForDuplicateCheck, schedule.entryDate]);

  // REVIEW FP-016: drop the Object.keys→Number(id) round-trip; the values
  // are the source of truth for active-row counting.
  const activeCount = useMemo(
    () => Object.values(states).filter((s) => s === 'work' || s === 'idle').length,
    [states],
  );

  function onStateChange(machineId: number, next: BulkRowState): void {
    setStates((prev) => ({ ...prev, [machineId]: next }));
    // Clear per-row error when user re-toggles state.
    setRowErrors((prev) => {
      if (prev[machineId] === undefined || prev[machineId] === null) return prev;
      const out = { ...prev };
      out[machineId] = null;
      return out;
    });
    // Toggling to Skip drops any staged files for that row — they're
    // ephemeral until upload, so no confirm is needed.
    if (next === 'skip') {
      setRows((prev) => {
        const current = prev[machineId];
        if (current === undefined || current.stagedFiles.length === 0) return prev;
        return { ...prev, [machineId]: { ...current, stagedFiles: [] } };
      });
    }
  }

  function onRowChange(machineId: number, patch: Partial<BulkRowDraft>): void {
    setRows((prev) => {
      const current = prev[machineId] ?? emptyRow();
      return { ...prev, [machineId]: { ...current, ...patch } };
    });
  }

  async function handleSave(): Promise<void> {
    // Gate on batch-level timing — moved here from step 3, so step 4 owns
    // the validation now.
    if (!/^\d{2}:\d{2}$/u.test(startTimeHm)) {
      setTimingError('Start time is required.');
      return;
    }
    if (endTimeHm !== '' && !/^\d{2}:\d{2}$/u.test(endTimeHm)) {
      setTimingError('End time must be HH:mm.');
      return;
    }
    if (endTimeHm !== '' && endTimeHm <= startTimeHm) {
      setTimingError('End time must be after start time.');
      return;
    }
    setTimingError(null);
    const body: BulkRowBody[] = [];
    const machineIdsInOrder: number[] = [];
    // Per-row staged-file lists, parallel to body[]. Index i in this array
    // pairs with createdIds[i] from BulkCreateResult per backend ordering
    // contract (BulkCreateLogsheetMakerResult docstring + integration tests).
    const stagedByRow: (readonly File[])[] = [];
    for (const m of machines) {
      const state = states[m.machineId] ?? 'skip';
      if (state === 'skip') continue;
      const draft = rows[m.machineId] ?? emptyRow();
      const kpis = kpisByMachine[m.machineId];
      machineIdsInOrder.push(m.machineId);
      stagedByRow.push(draft.stagedFiles);
      body.push({
        machineId: m.machineId,
        type: state === 'work' ? 'Work' : 'Idle',
        workDescription:
          state === 'work' && draft.workDescription !== ''
            ? draft.workDescription
            : null,
        idleReason:
          state === 'idle' && draft.idleReason !== '' ? draft.idleReason : null,
        notes: draft.notes !== '' ? draft.notes : null,
        kpiOverrides: state === 'work' ? buildKpiValuesForRow(kpis, draft) : null,
      });
    }
    if (body.length === 0) return;
    setSubmitting(true);
    setRowErrors({});
    setBulkErrors([]);
    try {
      const result = await onSubmit(body);

      // ── Per-row staged-file uploads ─────────────────────────────────
      // Walk createdIds (ordered to match request rows[]) and fire the
      // upload helper per row that has staged files. Aggregate per-file
      // outcomes so a single toast summarises the whole batch.
      let totalAttempted = 0;
      let okCount = 0;
      const failedFiles: { file: File; error: string }[] = [];
      for (let i = 0; i < result.createdIds.length; i += 1) {
        const files = stagedByRow[i] ?? [];
        if (files.length === 0) continue;
        const createdId = result.createdIds[i];
        if (createdId === undefined) continue;
        totalAttempted += files.length;
        const partition = await uploadStagedFiles(createdId, files);
        okCount += partition.ok.length;
        for (const f of partition.failed) failedFiles.push(f);
      }

      // ── Toast summary ───────────────────────────────────────────────
      const entryCount = result.totalCreated;
      if (totalAttempted === 0) {
        toast.success(`${String(entryCount)} draft${entryCount === 1 ? '' : 's'} created`);
      } else if (failedFiles.length === 0) {
        toast.success(
          `${String(entryCount)} draft${entryCount === 1 ? '' : 's'} created. ${String(totalAttempted)} document${totalAttempted === 1 ? '' : 's'} attached.`,
        );
      } else if (failedFiles.length === totalAttempted) {
        toast.warning(
          `${String(entryCount)} draft${entryCount === 1 ? '' : 's'} created. No documents attached.`,
          {
            description: `Failed: ${failedFiles.map((f) => f.file.name).join(', ')}. Open each entry to retry.`,
          },
        );
      } else {
        toast.warning(
          `${String(entryCount)} draft${entryCount === 1 ? '' : 's'} created. ${String(okCount)} of ${String(totalAttempted)} documents attached.`,
          {
            description: `Failed: ${failedFiles.map((f) => f.file.name).join(', ')}. Open the affected entries to retry.`,
          },
        );
      }

      onComplete(result);
    } catch (err: unknown) {
      if (err instanceof BulkValidationError) {
        const byRow: Record<number, BulkRowError | null> = {};
        const bulkOnly: BulkRowError[] = [];
        for (const e of err.errors) {
          if (e.rowIndex === -1) {
            bulkOnly.push(e);
            continue;
          }
          const mid = machineIdsInOrder[e.rowIndex];
          if (mid !== undefined) {
            byRow[mid] = e;
          }
        }
        setRowErrors(byRow);
        setBulkErrors(bulkOnly);
      } else {
        // REVIEW H-1 fix — any non-validation failure (network, 401, 403, 500,
        // axios timeout) reaches here. Without this branch, the spinner clears
        // silently and the maker walks away thinking the batch saved. Surface
        // the failure explicitly so the maker knows their work didn't persist
        // and can retry.
        const message = err instanceof Error ? err.message : 'Failed to save the batch.';
        toast.error(`Bulk save failed: ${message}`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 pb-24 md:pb-0" data-testid="bulk-step-machines">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm text-muted-foreground">Work order</p>
          <p className="font-medium">{workOrder.woNumber}</p>
        </div>
        <p
          className="text-sm text-muted-foreground"
          data-testid="bulk-machines-count-active"
        >
          {activeCount} of {machines.length} active
        </p>
      </div>

      {/* Timing — batch-level Start / End time. Moved here from step 3 so
          step 3 stays focused on date + shift. Applied to every row (Q7). */}
      <div
        className="rounded-md border border-input bg-muted/30 p-4 space-y-3"
        data-testid="bulk-machines-timing"
      >
        <div>
          <p className="text-sm font-medium">Timing</p>
          <p className="text-xs text-muted-foreground">
            Applied to every machine you mark as Worked or Idle.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="bulk-machines-start-time-hour">Start time *</Label>
            {/* Custom control (not native <input type="time">) so the 12-hour
                format is identical on macOS and Windows. */}
            <TimeField
              id="bulk-machines-start-time-hour"
              testId="bulk-machines-start-time"
              value={startTimeHm}
              onChange={updateStartTime}
              invalid={timingError !== null}
              describedBy={timingError !== null ? 'bulk-machines-timing-error' : undefined}
              className="min-h-11"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bulk-machines-end-time-hour">End time</Label>
            <TimeField
              id="bulk-machines-end-time-hour"
              testId="bulk-machines-end-time"
              value={endTimeHm}
              onChange={updateEndTime}
              invalid={timingError !== null}
              describedBy={timingError !== null ? 'bulk-machines-timing-error' : undefined}
              className="min-h-11"
            />
          </div>
        </div>
        {timingError !== null && (
          <p
            id="bulk-machines-timing-error"
            className="text-xs text-destructive"
            data-testid="bulk-machines-timing-error"
          >
            {timingError}
          </p>
        )}
      </div>

      {bulkErrors.length > 0 && (
        <div
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
          role="alert"
          data-testid="bulk-machines-bulk-error"
        >
          <p className="font-medium">We couldn&apos;t save:</p>
          <ul className="ml-4 list-disc">
            {bulkErrors.map((e, idx) => (
              <li key={`${e.errorCode}-${String(idx)}`}>{e.message}</li>
            ))}
          </ul>
        </div>
      )}

      {machines.length === 0 ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="bulk-machines-empty"
        >
          This work order has no machines.
        </p>
      ) : (
        <div className="space-y-3">
          {machines.map((m) => {
            const machineId = m.machineId;
            const state = states[machineId] ?? 'skip';
            const draft = rows[machineId] ?? emptyRow();
            const kpis = kpisByMachine[machineId];
            const dup = duplicates[machineId] ?? null;
            const rowError = rowErrors[machineId] ?? null;
            return (
              <BulkMachineCard
                key={machineId}
                machine={{
                  machineId,
                  machineCode: m.machineCode,
                  machineName: m.machineName,
                  vehicleNumber: m.vehicleNumber,
                }}
                state={state}
                row={draft}
                kpis={kpis}
                duplicateBadge={dup}
                rowError={rowError}
                onStateChange={(next) => {
                  onStateChange(machineId, next);
                }}
                onRowChange={(patch) => {
                  onRowChange(machineId, patch);
                }}
              />
            );
          })}
        </div>
      )}

      {/* Sticky bottom CTA on mobile; inline action row on desktop. */}
      <div className="fixed bottom-0 left-0 right-0 z-10 border-t bg-background p-3 shadow-lg md:hidden">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            disabled={submitting}
            data-testid="bulk-machines-back"
            className="flex-1 min-h-11"
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => {
              void handleSave();
            }}
            disabled={activeCount === 0 || submitting}
            data-testid="bulk-machines-save"
            className="flex-1 min-h-11"
          >
            {submitting
              ? 'Saving…'
              : `Save ${String(activeCount)} Draft${activeCount === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>

      <div className="hidden items-center justify-between gap-2 pt-2 md:flex">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={submitting}
          data-testid="bulk-machines-back-desktop"
        >
          Back
        </Button>
        <Button
          type="button"
          onClick={() => {
            void handleSave();
          }}
          disabled={activeCount === 0 || submitting}
          data-testid="bulk-machines-save-desktop"
        >
          {submitting
            ? 'Saving…'
            : `Save ${String(activeCount)} Draft${activeCount === 1 ? '' : 's'}`}
        </Button>
      </div>
    </div>
  );
}
